import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Radio, InputNumber, Switch, Button, Divider, message, ColorPicker, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { leavesSettingsApi, show422Error, ValidationError, departmentsApi, designationsApi, rolesApi } from '../../../utils/api';
import { scrollToFirstError, translateRoleName } from '../../../utils/formHelpers';
import { BulkSelect } from '../../common/bulkSelectProps';

const { Option } = Select;

const LeaveTypeModal = ({
  visible,
  onCancel,
  onSuccess,
  editingLeaveType,
  departments: propDepartments = [],
  designations: propDesignations = [],
  roles: propRoles = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [leaveType, setLeaveType] = useState('monthly');
  const [departments, setDepartments] = useState(propDepartments);
  const [designations, setDesignations] = useState(propDesignations);
  const [roles, setRoles] = useState(propRoles);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const unusedLeaveWatch = Form.useWatch('unused_leave', form);

  // بارگذاری departments, designations, roles از API
  useEffect(() => {
    if (visible) {
      setOptionsLoaded(false);
      const loadOptions = async () => {
        try {
          const [deptRes, desRes, rolesRes] = await Promise.all([
            departmentsApi.getAll(),
            designationsApi.getAll(),
            rolesApi.getAll(),
          ]);
          setDepartments(deptRes.data || []);
          setDesignations(desRes.data || []);
          setRoles(Array.isArray(rolesRes) ? rolesRes : []);
          setOptionsLoaded(true);
        } catch (error) {
          console.error('خطا در دریافت گزینه‌ها:', error);
          setOptionsLoaded(true);
        }
      };
      loadOptions();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && optionsLoaded) {
      if (editingLeaveType) {
        // Parse JSON strings to arrays
        const gender = typeof editingLeaveType.gender === 'string' 
          ? JSON.parse(editingLeaveType.gender) 
          : editingLeaveType.gender || [];
        const maritalStatus = typeof editingLeaveType.marital_status === 'string'
          ? JSON.parse(editingLeaveType.marital_status)
          : editingLeaveType.marital_status || [];
        const department = typeof editingLeaveType.department === 'string'
          ? JSON.parse(editingLeaveType.department).map(Number)
          : editingLeaveType.department || [];
        const designation = typeof editingLeaveType.designation === 'string'
          ? JSON.parse(editingLeaveType.designation).map(Number)
          : editingLeaveType.designation || [];
        const role = typeof editingLeaveType.role === 'string'
          ? JSON.parse(editingLeaveType.role).map(Number)
          : editingLeaveType.role || [];

        form.setFieldsValue({
          type_name: editingLeaveType.type_name,
          leavetype: editingLeaveType.leavetype,
          color: editingLeaveType.color,
          paid: editingLeaveType.paid === 1,
          monthly_leave_number: editingLeaveType.leavetype === 'monthly' ? editingLeaveType.no_of_leaves : null,
          yearly_leave_number: editingLeaveType.leavetype === 'yearly' ? editingLeaveType.no_of_leaves : null,
          monthly_limit: editingLeaveType.monthly_limit || null,
          effective_after: editingLeaveType.effective_after || null,
          effective_type: editingLeaveType.effective_type || 'day',
          unused_leave: editingLeaveType.unused_leave || 'carry forward',
          max_carry_forward: editingLeaveType.max_carry_forward ?? null,
          requires_file: Boolean(editingLeaveType.requires_file),
          requires_approval:
            editingLeaveType.requires_approval === undefined
              ? true
              : Boolean(editingLeaveType.requires_approval),
          min_advance_days: editingLeaveType.min_advance_days ?? 0,
          max_consecutive_days: editingLeaveType.max_consecutive_days ?? null,
          over_utilization: editingLeaveType.over_utilization || 'not_allowed',
          encashed: editingLeaveType.encashed || '',
          allowed_probation: editingLeaveType.allowed_probation === 1 || editingLeaveType.allowed_probation === 'on' ? 'on' : 'off',
          allowed_notice: editingLeaveType.allowed_notice === 1 || editingLeaveType.allowed_notice === 'on' ? 'on' : 'off',
          gender,
          marital_status: maritalStatus,
          department,
          designation,
          role,
        });
        setLeaveType(editingLeaveType.leavetype);
      } else {
        form.resetFields();
        form.setFieldsValue({
          leavetype: 'monthly',
          color: '#16813D',
          paid: false,
          effective_type: 'day',
          unused_leave: 'lapse',
          max_carry_forward: null,
          requires_file: false,
          requires_approval: true,
          min_advance_days: 0,
          max_consecutive_days: null,
          over_utilization: 'not_allowed',
          allowed_probation: 'off',
          allowed_notice: 'off',
          encashed: '',
          gender: [],
          marital_status: [],
          department: [],
          designation: [],
          role: [],
        });
        setLeaveType('monthly');
      }
    }
  }, [visible, editingLeaveType, form, optionsLoaded]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare payload according to the provided structure
      const payload = {
        type_name: values.type_name,
        leavetype: values.leavetype,
        color: typeof values.color === 'string' ? values.color : values.color?.toHexString() || '#16813D',
        paid: values.paid ? 1 : 0,
        monthly_leave_number: values.leavetype === 'monthly' ? values.monthly_leave_number : null,
        yearly_leave_number: values.leavetype === 'yearly' ? values.yearly_leave_number : null,
        monthly_limit: values.monthly_limit || null,
        effective_after: values.effective_after || null,
        effective_type: values.effective_type,
        unused_leave: values.unused_leave,
        max_carry_forward:
          values.unused_leave === 'carry forward' ? values.max_carry_forward ?? null : null,
        requires_file: Boolean(values.requires_file),
        requires_approval: values.requires_approval !== false,
        min_advance_days: values.min_advance_days ?? 0,
        max_consecutive_days: values.max_consecutive_days ?? null,
        over_utilization: values.over_utilization,
        encashed: values.encashed || '',
        allowed_probation: values.allowed_probation === 'on' ? 'on' : 'off',
        allowed_notice: values.allowed_notice === 'on' ? 'on' : 'off',
        gender: values.gender || [],
        marital_status: values.marital_status || [],
        department: values.department || [],
        designation: values.designation || [],
        role: values.role || [],
      };

      if (editingLeaveType) {
        await leavesSettingsApi.updateLeaveType(editingLeaveType.id, payload);
        message.success('نوع مرخصی با موفقیت ویرایش شد');
      } else {
        await leavesSettingsApi.createLeaveType(payload);
        message.success('نوع مرخصی با موفقیت ایجاد شد');
      }

      onSuccess();
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره نوع مرخصی');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveTypeChange = (e) => {
    const value = e.target.value;
    setLeaveType(value);
    if (value === 'monthly') {
      form.setFieldsValue({ yearly_leave_number: null });
    } else {
      form.setFieldsValue({ monthly_leave_number: null });
    }
  };

  const genderOptions = [
    { label: 'مرد', value: 'male' },
    { label: 'زن', value: 'female' },
  ];

  const maritalStatusOptions = [
    { label: 'مجرد', value: 'single' },
    { label: 'متاهل', value: 'married' },
  ];

  return (
    <Modal
      title={
        <div>
          <div className="text-xl font-bold" style={{ fontFamily: 'CustomFont' }}>
            {editingLeaveType ? 'ویرایش نوع مرخصی' : 'افزودن نوع مرخصی جدید'}
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      style={{ fontFamily: 'CustomFont' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ fontFamily: 'CustomFont' }}
      >
        {/* بخش عمومی */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'CustomFont' }}>
            اطلاعات عمومی
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  نام نوع مرخصی
                </span>
              }
              name="type_name"
              rules={[{ required: true, message: 'لطفاً نام نوع مرخصی را وارد کنید' }]}
            >
              <Input placeholder="مثلاً: استعلاجی، مرخصی عادی" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  کد رنگ
                </span>
              }
              name="color"
              rules={[{ required: true, message: 'لطفاً رنگ را انتخاب کنید' }]}
            >
              <ColorPicker showText format="hex" />
            </Form.Item>
          </div>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>نوع تخصیص مرخصی</span>}
            name="leavetype"
            rules={[{ required: true, message: 'لطفاً نوع مرخصی را انتخاب کنید' }]}
          >
            <Radio.Group onChange={handleLeaveTypeChange}>
              <Radio value="monthly">ماهانه</Radio>
              <Radio value="yearly">سالانه</Radio>
            </Radio.Group>
          </Form.Item>

          {leaveType === 'monthly' && (
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  تعداد مرخصی ماهانه{' '}
                  <Tooltip title="فیلد برای تعیین تعداد مرخصی ماهانه قابل استفاده توسط کارمندان است">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="monthly_leave_number"
              rules={[{ required: true, message: 'لطفاً تعداد مرخصی ماهانه را وارد کنید' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}

          {leaveType === 'yearly' && (
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>تعداد مرخصی سالانه</span>}
              name="yearly_leave_number"
              rules={[{ required: true, message: 'لطفاً تعداد مرخصی سالانه را وارد کنید' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Form.Item
            label={
              <span style={{ fontFamily: 'CustomFont' }}>
                محدودیت ماهانه
              </span>
            }
            name="monthly_limit"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="اختیاری" />
          </Form.Item>

          <Form.Item
            label={
              <span style={{ fontFamily: 'CustomFont' }}>
                وضعیت پرداخت مرخصی{' '}
                <Tooltip title="این مرخصی برای همه کارکنان به صورت «با حقوق» یا «بدون حقوق» خواهد بود">
                  <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                </Tooltip>
              </span>
            }
            name="paid"
            valuePropName="checked"
          >
            <Switch checkedChildren="با حقوق" unCheckedChildren="بدون حقوق" />
          </Form.Item>
        </div>

        <Divider />

        {/* بخش استحقاق */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'CustomFont' }}>
            تنظیمات استحقاق
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  اثر از{' '}
                </span>
              }
              name="effective_type"
            >
              <Select style={{ fontFamily: 'CustomFont' }}>
                <Option value="day">روز</Option>
                <Option value="month">ماه</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>تعداد روز/ماه پس از استخدام</span>}
              name="effective_after"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="اختیاری" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  مرخصی های استفاده نشده{' '}
                  <Tooltip
                    title={
                      <div style={{ fontFamily: 'CustomFont', direction: 'rtl', textAlign: 'right' }}>
                        <div style={{ marginBottom: '8px' }}>نحوه مدیریت مرخصی‌های استفاده‌نشده در پایان سال را مشخص می‌کند:</div>
                        <div style={{ marginBottom: '4px' }}>• انتقال: مرخصی‌های استفاده‌نشده به سال بعد منتقل می‌شوند</div>
                        <div style={{ marginBottom: '4px' }}>• منقضی شود: مرخصی‌های استفاده‌نشده حذف می‌شوند و اقدامی صورت نمی‌گیرد</div>
                        <div style={{ marginBottom: '4px' }}>• پرداخت شود: بابت روزهای مرخصی استفاده‌نشده حقوق پرداخت می‌شود</div>
                        <div style={{ marginTop: '8px', color: '#ff4d4f', fontSize: '12px' }}>
                          توجه: پس از ایجاد نوع مرخصی، این گزینه دیگر قابل تغییر نیست
                        </div>
                      </div>
                    }
                  >
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="unused_leave"
              rules={[{ required: true, message: 'لطفاً گزینه را انتخاب کنید' }]}
            >
              <Select style={{ fontFamily: 'CustomFont' }}>
                <Option value="carry forward">انتقال به سال بعد</Option>
                <Option value="lapse">از بین می‌رود</Option>
                <Option value="paid">پرداخت می‌شود</Option>
                <Option value="encash">بازخرید نقدی</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  استفاده بیش از حد{' '}
                  <Tooltip
                    title={
                      <div style={{ fontFamily: 'CustomFont', direction: 'rtl', textAlign: 'right' }}>
                        <div style={{ marginBottom: '8px' }}>نحوه‌ی مدیریت استفاده‌ی بیش از حد از این نوع مرخصی:</div>
                        <div style={{ marginBottom: '4px' }}>• غیرمجاز: استفاده بیشتر از تعداد مجاز امکان‌پذیر نیست</div>
                        <div style={{ marginBottom: '4px' }}>• مجاز با حقوق: استفاده بیشتر از حد مجاز است و مرخصی اضافه با حقوق محاسبه می‌شود</div>
                        <div>• مجاز بدون حقوق: استفاده بیشتر از حد مجاز است ولی مرخصی اضافه بدون حقوق خواهد بود</div>
                      </div>
                    }
                  >
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="over_utilization"
              rules={[{ required: true, message: 'لطفاً گزینه را انتخاب کنید' }]}
            >
              <Select style={{ fontFamily: 'CustomFont' }}>
                <Option value="not_allowed">مجاز نیست</Option>
                <Option value="allow_paid">مجاز با حقوق</Option>
                <Option value="allow_unpaid">مجاز بدون حقوق</Option>
              </Select>
            </Form.Item>
          </div>

          {unusedLeaveWatch === 'carry forward' ? (
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  حداکثر انتقال به سال بعد{' '}
                  <Tooltip title="سقف روزهایی که می‌توانند به سال بعد منتقل شوند (مثلاً حداکثر ۵ روز)">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="max_carry_forward"
              rules={[
                {
                  required: true,
                  message: 'حداکثر روز قابل انتقال را وارد کنید',
                },
              ]}
            >
              <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="مثلاً ۵" />
            </Form.Item>
          ) : null}
        </div>

        <Divider />

        {/* سیاست‌های درخواست و تأیید */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'CustomFont' }}>
            سیاست‌های درخواست و تأیید
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  نیاز به فایل / مدرک؟{' '}
                  <Tooltip title="اگر فعال باشد، کارمند باید هنگام ثبت این نوع مرخصی فایل پیوست کند (مثلاً گواهی پزشک)">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="requires_file"
              valuePropName="checked"
            >
              <Switch checkedChildren="الزامی" unCheckedChildren="اختیاری" />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  نیاز به تأیید مدیر؟{' '}
                  <Tooltip title="اگر غیرفعال باشد، مرخصی بلافاصله تأیید خودکار می‌شود">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="requires_approval"
              valuePropName="checked"
            >
              <Switch checkedChildren="نیاز به تأیید" unCheckedChildren="تأیید خودکار" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  حداقل روز درخواست قبل از مرخصی{' '}
                  <Tooltip title="کارمند باید حداقل چند روز قبل از تاریخ شروع، درخواست را ثبت کند (۰ = بدون محدودیت)">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="min_advance_days"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="مثلاً ۲" />
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  حداکثر روز متوالی مجاز{' '}
                  <Tooltip title="سقف تعداد روزهای کاری پیوسته در یک درخواست (خالی = بدون سقف)">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="max_consecutive_days"
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="مثلاً ۵" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  مجاز در دوره آزمایشی{' '}
                  <Tooltip title="اگر می‌خواهید کارمندان در دوره آزمایشی بتوانند از این مرخصی استفاده کنند این گزینه را فعال کنید">
                    <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              }
              name="allowed_probation"
            >
              <Radio.Group>
                <Radio value="on">بله</Radio>
                <Radio value="off">خیر</Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        </div>

        <Divider />

        {/* بخش قابلیت اجرا */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'CustomFont' }}>
            قابلیت اجرا
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  جنسیت{' '}
                </span>
              }
              name="gender"
            >
              <BulkSelect
                mode="multiple"
                placeholder="انتخاب جنسیت"
                style={{ fontFamily: 'CustomFont' }}
                allValues={genderOptions.map((o) => o.value)}
              >
                {genderOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </BulkSelect>
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  وضعیت تأهل{' '}
                </span>
              }
              name="marital_status"
            >
              <BulkSelect
                mode="multiple"
                placeholder="انتخاب وضعیت تاهل"
                style={{ fontFamily: 'CustomFont' }}
                allValues={maritalStatusOptions.map((o) => o.value)}
              >
                {maritalStatusOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </BulkSelect>
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  دپارتمان{' '}
                </span>
              }
              name="department"
            >
              <BulkSelect
                mode="multiple"
                placeholder="انتخاب دپارتمان"
                style={{ fontFamily: 'CustomFont' }}
                allValues={departments.map((d) => d.id)}
              >
                {departments.map((dept) => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.team_name || dept.name}
                  </Option>
                ))}
              </BulkSelect>
            </Form.Item>

            <Form.Item
              label={
                <span style={{ fontFamily: 'CustomFont' }}>
                  عنوان شغلی{' '}
                </span>
              }
              name="designation"
            >
              <BulkSelect
                mode="multiple"
                placeholder="انتخاب سمت"
                style={{ fontFamily: 'CustomFont' }}
                allValues={designations.map((d) => d.id)}
              >
                {designations.map((des) => (
                  <Option key={des.id} value={des.id}>
                    {des.name}
                  </Option>
                ))}
              </BulkSelect>
            </Form.Item>
          </div>

          <Form.Item
            label={
              <span style={{ fontFamily: 'CustomFont' }}>
                نقش کاربر{' '}
              </span>
            }
            name="role"
          >
            <BulkSelect
              mode="multiple"
              placeholder="انتخاب نقش"
              style={{ fontFamily: 'CustomFont' }}
              allValues={roles.map((r) => r.id)}
            >
              {roles.map((r) => (
                <Option key={r.id} value={r.id}>
                  {translateRoleName(r.display_name || r.name)}
                </Option>
              ))}
            </BulkSelect>
          </Form.Item>
        </div>

        <Divider />

        <div className="flex items-center justify-end gap-4 pt-4">
          <Button onClick={onCancel} size="middle" style={{ fontFamily: 'CustomFont' }} disabled={loading}>
            لغو
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="middle"
            className="bg-green-600 hover:bg-green-700 border-green-600"
            style={{ fontFamily: 'CustomFont' }}
            loading={loading}
          >
            ذخیره
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default LeaveTypeModal;
